import React, { useState, useEffect } from 'react';
import { FlashcardSet } from '../types';
import { getTopicStats } from '../utils/firebaseStorage';
import { getFlashcardSetAccess } from '../catalog/flashcards';
import { useEntitlements } from '../hooks/useEntitlements';
import { BookOpen, TrendingUp, Flag, Gavel, Lock, CheckCircle2, Play, Lightbulb, Heart, Landmark, Users, BookText } from 'lucide-react';

interface TopicSelectionProps {
  flashcardSets: FlashcardSet[];
  onTopicSelect: (set: FlashcardSet) => void;
  className?: string;
  onNavigate?: (section: string) => void;
}

interface TopicProgress {
  [topicId: string]: {
    completed: boolean;
    progress: number;
    knownCount: number;
    unknownCount: number;
    totalCards: number;
  };
}

const TopicSelection: React.FC<TopicSelectionProps> = ({
  flashcardSets,
  onTopicSelect,
  className = '',
  onNavigate
}) => {
  const { hasPlus } = useEntitlements();
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [topicProgress, setTopicProgress] = useState<TopicProgress>({});

  // Load progress data for all topics on component mount
  useEffect(() => {
    const loadAllProgress = async () => {
      const progressData: TopicProgress = {};

      // Use Promise.all to load all progress data concurrently
      await Promise.all(
        flashcardSets.map(async (set) => {
          const stats = await getTopicStats(set.id);
          if (stats) {
            progressData[set.id] = stats;
          } else {
            // No progress data, set defaults
            progressData[set.id] = {
              completed: false,
              progress: 0,
              knownCount: 0,
              unknownCount: 0,
              totalCards: set.cards.length
            };
          }
        })
      );

      setTopicProgress(progressData);
    };

    loadAllProgress();
  }, [flashcardSets]);

  const handleTopicClick = (set: FlashcardSet) => {
    const isLocked = getFlashcardSetAccess(set.id) === 'pro' && !hasPlus;

    if (isLocked) {
      onNavigate?.('upgrade');
      return;
    }

    setSelectedTopic(set.id);
    // Add a small delay for visual feedback before navigating
    setTimeout(() => {
      onTopicSelect(set);
    }, 150);
  };

  // Map topic IDs to lucide-react icons
  const getTopicIcon = (topicId: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      'ekonomie': <TrendingUp className="w-5 h-5" />,
      'evropska-integrace': <Flag className="w-5 h-5" />,
      'moderni-historie': <BookText className="w-5 h-5" />,
      'politologie': <Landmark className="w-5 h-5" />,
      'filosofie': <Lightbulb className="w-5 h-5" />,
      'psychologie': <Heart className="w-5 h-5" />,
      'pravo': <Gavel className="w-5 h-5" />,
      'sociologie': <Users className="w-5 h-5" />
    };
    return iconMap[topicId] || <BookOpen className="w-5 h-5" />;
  };

  // Get icon color based on topic
  const getIconColor = (topicId: string, isCompleted: boolean, isLocked: boolean) => {
    if (isLocked) return 'text-gray-400 dark:text-gray-500';
    if (isCompleted) return 'text-green-600 dark:text-green-400';

    const colorMap: { [key: string]: string } = {
      'ekonomie': 'text-green-600 dark:text-green-400',
      'evropska-integrace': 'text-blue-600 dark:text-blue-400',
      'moderni-historie': 'text-amber-600 dark:text-amber-400',
      'politologie': 'text-purple-600 dark:text-purple-400',
      'filosofie': 'text-indigo-600 dark:text-indigo-400',
      'psychologie': 'text-pink-600 dark:text-pink-400',
      'pravo': 'text-orange-600 dark:text-orange-400',
      'sociologie': 'text-teal-600 dark:text-teal-400'
    };
    return colorMap[topicId] || 'text-gray-600 dark:text-gray-400';
  };

  // Get background color for icon circle
  const getIconBgColor = (topicId: string, isCompleted: boolean, isLocked: boolean) => {
    if (isLocked) return 'bg-gray-100 dark:bg-gray-700';
    if (isCompleted) return 'bg-green-100 dark:bg-green-900';

    const bgMap: { [key: string]: string } = {
      'ekonomie': 'bg-green-100 dark:bg-green-900',
      'evropska-integrace': 'bg-blue-100 dark:bg-blue-900',
      'moderni-historie': 'bg-amber-100 dark:bg-amber-900',
      'politologie': 'bg-purple-100 dark:bg-purple-900',
      'filosofie': 'bg-indigo-100 dark:bg-indigo-900',
      'psychologie': 'bg-pink-100 dark:bg-pink-900',
      'pravo': 'bg-orange-100 dark:bg-orange-900',
      'sociologie': 'bg-teal-100 dark:bg-teal-900'
    };
    return bgMap[topicId] || 'bg-gray-100 dark:bg-gray-700';
  };

  if (flashcardSets.length === 0) {
    return (
      <div className={`flex items-center justify-center min-h-96 ${className}`}>
        <div className="text-center">
          <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
            Žádná témata nejsou k dispozici
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Flashkarty se načítají nebo nejsou dostupné.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`topic-selection ${className}`}>
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Vyberte téma pro studium
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Zvolte si oblast, kterou chcete studovat pomocí flashkaret
          </p>
        </div>

        {/* Topics List */}
        <div className="space-y-3">
          {flashcardSets.map((set) => {
            const isLocked = getFlashcardSetAccess(set.id) === 'pro' && !hasPlus;
            const progress = topicProgress[set.id];
            const isCompleted = progress?.completed || false;
            const hasProgress = progress && progress.progress > 0;

            return (
              <button
                key={set.id}
                onClick={() => handleTopicClick(set)}
                disabled={selectedTopic === set.id}
                className={`
                  group
                  w-full flex items-center p-4 rounded-lg
                  border
                  ${isCompleted && !isLocked
                    ? 'border-green-500 dark:border-green-400 shadow-[0_0_15px_rgba(34,197,94,0.2)] dark:shadow-[0_0_15px_rgba(74,222,128,0.3)]'
                    : 'border-border'
                  }
                  bg-card
                  transition-all duration-200
                  ${selectedTopic === set.id
                    ? 'opacity-75 cursor-wait'
                    : 'hover:bg-accent hover:shadow-md cursor-pointer'
                  }
                  focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
                `}
                aria-label={`Select topic: ${set.title}`}
              >
                {/* Icon */}
                <div className="flex-shrink-0 mr-4">
                  <div className={`
                    w-12 h-12 rounded-full flex items-center justify-center
                    transition-colors duration-200
                    ${getIconBgColor(set.id, isCompleted, isLocked)}
                    group-hover:scale-110
                  `}>
                    {isLocked ? (
                      <Lock className="w-5 h-5 text-gray-500" />
                    ) : isCompleted ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                    ) : (
                      <div className={getIconColor(set.id, isCompleted, isLocked)}>
                        {getTopicIcon(set.id)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 text-left">
                  {/* Title and badges */}
                  <div className="flex items-center mb-1">
                    <h3 className="text-lg font-semibold text-foreground group-hover:text-gray-900 dark:group-hover:text-white truncate transition-colors">
                      {set.title}
                    </h3>
                    {isLocked && (
                      <div className="ml-2 bg-yellow-400 text-yellow-950 text-xs font-black tracking-wide px-2 py-0.5 rounded-md shadow-sm border-b-2 border-yellow-600 flex-shrink-0">
                        PREMIUM
                      </div>
                    )}
                    {isCompleted && !isLocked && (
                      <span className="ml-2 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full flex-shrink-0 font-semibold flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        DOKONČENO
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  {set.description && (
                    <p className="text-sm text-muted-foreground group-hover:text-gray-900 dark:group-hover:text-white mb-2 line-clamp-1 transition-colors">
                      {set.description}
                    </p>
                  )}

                  {/* Progress bar */}
                  {hasProgress && !isLocked && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                        <span>{progress.progress}% dokončeno</span>
                        <div className="flex items-center space-x-2">
                          <span className="flex items-center text-green-600 dark:text-green-400">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            {progress.knownCount}
                          </span>
                          {progress.unknownCount > 0 && (
                            <span className="flex items-center text-red-600 dark:text-red-400">
                              <span className="mr-1">✗</span>
                              {progress.unknownCount}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all duration-500 ease-out"
                          style={{ width: `${progress.progress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Card count for topics without progress */}
                  {!hasProgress && (
                    <div className="flex items-center text-sm text-muted-foreground">
                      <BookOpen className="w-4 h-4 mr-1" />
                      <span>{set.cards.length} karet</span>
                    </div>
                  )}
                </div>

                {/* Action icon */}
                <div className="flex-shrink-0 ml-4">
                  {selectedTopic === set.id ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                  ) : (
                    <Play className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Loading State for Selected Topic */}
      {selectedTopic && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 text-center shadow-xl">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-foreground">
              Načítám téma...
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopicSelection;